import { Link } from 'react-router-dom';
import { useTitle } from '@/lib/useTitle';
import { useJsonLd } from '@/lib/useJsonLd';
import DimensionLine from '@/components/DimensionLine';

export const meta = {
  path: '/year-end-commercial-checklist-kwc',
  title: 'Your Year-End Commercial Checklist for Kitchener-Waterloo-Cambridge',
  description: 'October is the last calm month for KWC commercial real estate. Three moves before year-end: industrial shortlists, the closing sublease window, and pricing with data. Pinnacle Commercial.',
};

const moves = [
  {
    no: '01',
    tag: 'Industrial occupiers',
    heading: 'Build the shortlist this month.',
    body: [
      'Region-wide vacancy is 7.4% and the market absorbed over 205,000 sq ft in Q2 — available space gets taken. Kitchener (7.0% vacancy) is the tightest submarket; Cambridge (8.0%) gives you more negotiating room.',
      'If you need space in the next 6-12 months, the options list should exist before the year-end rush, not during it.',
    ],
  },
  {
    no: '02',
    tag: 'Office tenants',
    heading: 'Stop waiting for the sublease bargain.',
    body: [
      'Sublease availability collapsed from 10.7% to 4.3% in one quarter. That overflow space is mostly gone, and with 107,000 sq ft of positive absorption, the remaining quality space goes to tenants who move with terms ready.',
    ],
  },
  {
    no: '03',
    tag: 'Owners',
    heading: 'Price with the data, not the hope.',
    body: [
      'Industrial asking rents sit near $12.95/sq ft net region-wide. Whether you are leasing, selling, or holding, Q3 close-out is the moment to set the number — December negotiates from weakness, October from information.',
    ],
  },
];

export default function YearEndCommercialChecklistKWC() {
  useTitle(meta.title, meta.description);
  useJsonLd('year-end-commercial-checklist-kwc', {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: meta.title,
    description: meta.description,
    datePublished: '2026-10-05',
    dateModified: '2026-10-05',
    mainEntityOfPage: 'https://pinnaclekwc.com/year-end-commercial-checklist-kwc',
    author: { '@type': 'Organization', name: 'Pinnacle Commercial' },
    publisher: { '@type': 'Organization', name: 'Pinnacle Commercial' },
    spatialCoverage: { '@type': 'Place', name: 'Kitchener-Waterloo-Cambridge, Ontario, Canada' },
  });

  return (
    <>
      <section className="border-b border-medline bg-grid-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
          <p className="annot">Pinnacle Commercial · Year-end playbook</p>
          <h1 className="mt-4 max-w-5xl font-display text-[38px] font-semibold leading-[1.03] tracking-tight sm:text-[58px]">Your year-end commercial checklist for Kitchener-Waterloo-Cambridge.</h1>
          <p className="mt-6 max-w-3xl text-[17px] leading-relaxed text-annot">October is the last calm month for commercial real estate in Kitchener-Waterloo-Cambridge. CBRE's Q2 2026 numbers make the case for moving now, not in December — three moves before year-end.</p>
          <p className="mt-5 font-mono text-[10px] uppercase tracking-wider2 text-medline">Published October 5, 2026 · Based on CBRE Q2 2026 research</p>
        </div>
      </section>

      <section className="bg-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Three moves before year-end" className="mb-10" />
          <div className="grid gap-px border border-medline bg-medline lg:grid-cols-3">
            {moves.map((move) => (
              <article key={move.no} className="bg-sheet p-6 sm:p-7">
                <p className="font-mono text-[10px] uppercase tracking-wider2 text-annot">{move.no} · {move.tag}</p>
                <h2 className="mt-3 font-display text-[26px] font-semibold leading-tight">{move.heading}</h2>
                <div className="mt-4 space-y-4 text-[14px] leading-relaxed text-graphite-600">
                  {move.body.map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-medline bg-table">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Start the conversation" className="mb-8" />
          <div className="max-w-4xl space-y-4 text-[15px] leading-relaxed text-graphite-600">
            <p>Buying, selling, or leasing commercial space in KWC or Guelph? Start the conversation at <Link to="/" className="text-redline hover:text-redline-600">pinnaclekwc.com</Link>.</p>
            <p className="text-[13px] text-annot"><strong>Sources:</strong> CBRE Research, Waterloo Region Industrial Figures Q2 2026; CBRE Research, Waterloo Region Office Figures Q2 2026.</p>
            <p className="text-[12px] text-annot">This page is market information, not an appraisal, investment recommendation or representation that a specific property will transact at a stated rent, price or cap rate.</p>
          </div>
          <div className="mt-10 flex flex-wrap gap-3"><Link to="/commercial-property-value-calculator" className="btn-primary">Model a property's NOI value</Link><Link to="/contact" className="btn-ghost">Discuss a Waterloo Region property</Link></div>
        </div>
      </section>
    </>
  );
}
